import { create } from 'zustand';
import { errorMessage } from '../lib/errors.js';
import { OfflineError } from '../api/client.js';
import { newTempId, type HabitPatch } from '../lib/offline/queue.js';
import { useOffline } from './offline.js';
import {
  type Habit,
  type HabitLog,
  listHabits,
  createHabit,
  updateHabit,
  deleteHabit,
  logHabit,
  unlogHabit,
} from '../api/habits.js';

// API Gateway throttles at 5 req/s (burst 10): a day of 11 habits fired at once
// can trip a 429, so the fan-out keeps at most this many requests in flight.
const MAX_IN_FLIGHT = 5;

export interface SetDayResult {
  written: string[]; // habit ids the server accepted — what an Undo has to revert
  failed: number;
}

interface HabitsState {
  habits: Habit[];
  logs: HabitLog[];
  loading: boolean;
  error: string | null;
  load: () => Promise<void>;
  create: (input: Parameters<typeof createHabit>[0]) => Promise<void>;
  update: (hid: string, patch: Partial<Habit>) => Promise<void>;
  remove: (hid: string) => Promise<void>;
  toggle: (hid: string, date: string) => Promise<void>;
  setDay: (date: string, habitIds: string[], done: boolean) => Promise<SetDayResult>;
  reorder: (habitIds: string[]) => Promise<void>;
  isDone: (hid: string, date: string) => boolean;
}

async function mapWithLimit<T>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<void>
): Promise<PromiseSettledResult<void>[]> {
  const results: PromiseSettledResult<void>[] = new Array(items.length);
  let next = 0;
  async function worker(): Promise<void> {
    while (next < items.length) {
      const i = next++;
      try {
        await fn(items[i]!);
        results[i] = { status: 'fulfilled', value: undefined };
      } catch (reason) {
        results[i] = { status: 'rejected', reason };
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

export const useHabits = create<HabitsState>((set, get) => ({
  habits: [],
  logs: [],
  // Rien n'est encore lu : la vue montre son squelette dès le premier rendu, pas un « No habits ».
  loading: true,
  error: null,
  load: async () => {
    set({ loading: true, error: null });
    try {
      const { habits, logs } = await listHabits();
      set({ habits: habits.sort((a, b) => a.order - b.order), logs, loading: false });
    } catch (err) {
      set({ error: errorMessage(err), loading: false });
    }
  },
  create: async (input) => {
    const payload = { ...input, order: Date.now() };
    try {
      const h = await createHabit(payload);
      set({ habits: [...get().habits, h].sort((a, b) => a.order - b.order) });
    } catch (err) {
      if (!(err instanceof OfflineError)) throw err;
      const tempId = newTempId();
      const h: Habit = {
        habitId: tempId,
        name: payload.name,
        emoji: payload.emoji ?? null,
        color: payload.color ?? null,
        order: payload.order,
        createdAt: new Date().toISOString(),
      };
      useOffline.getState().enqueue({ kind: 'habit.create', id: tempId, input: payload });
      set({ habits: [...get().habits, h].sort((a, b) => a.order - b.order) });
    }
  },
  update: async (hid, patch) => {
    const applyLocal = (next: Habit): void =>
      set({
        habits: get()
          .habits.map((h) => (h.habitId === hid ? next : h))
          .sort((a, b) => a.order - b.order),
      });
    try {
      applyLocal(await updateHabit(hid, patch));
    } catch (err) {
      if (!(err instanceof OfflineError)) throw err;
      const current = get().habits.find((h) => h.habitId === hid);
      useOffline.getState().enqueue({ kind: 'habit.update', id: hid, patch: patch as HabitPatch });
      if (current) applyLocal({ ...current, ...patch });
    }
  },
  remove: async (hid) => {
    try {
      await deleteHabit(hid);
    } catch (err) {
      if (!(err instanceof OfflineError)) throw err;
      useOffline.getState().enqueue({ kind: 'habit.delete', id: hid });
    }
    set({
      habits: get().habits.filter((h) => h.habitId !== hid),
      logs: get().logs.filter((l) => l.habitId !== hid),
    });
  },
  toggle: async (hid, date) => {
    const done = get().isDone(hid, date);
    try {
      if (done) await unlogHabit(hid, date);
      else await logHabit(hid, date);
    } catch (err) {
      if (!(err instanceof OfflineError)) throw err;
      useOffline
        .getState()
        .enqueue({ kind: done ? 'habit.unlog' : 'habit.log', id: hid, date });
    }
    set({
      logs: done
        ? get().logs.filter((l) => !(l.habitId === hid && l.date === date))
        : [...get().logs, { habitId: hid, date }],
    });
  },
  // Brings every listed habit to `done` on `date`, touching only the ones that
  // differ. Optimistic; on any failure the server's view replaces the guess.
  setDay: async (date, habitIds, done) => {
    const targets = habitIds.filter((hid) => get().isDone(hid, date) !== done);
    if (targets.length === 0) return { written: [], failed: 0 };

    const targetSet = new Set(targets);
    set({
      logs: done
        ? [...get().logs, ...targets.map((habitId) => ({ habitId, date }))]
        : get().logs.filter((l) => !(l.date === date && targetSet.has(l.habitId))),
    });

    const results = await mapWithLimit(targets, MAX_IN_FLIGHT, (hid) =>
      done ? logHabit(hid, date) : unlogHabit(hid, date)
    );
    const written = targets.filter((_, i) => results[i]?.status === 'fulfilled');

    // Un échec réseau n'est pas un échec : il part en file et sera rejoué, donc il ne
    // compte pas dans `failed` et ne doit surtout pas déclencher la resynchronisation
    // ci-dessous, qui écraserait l'état optimiste avec le cache du service worker.
    const queued = targets.filter((hid, i) => {
      if (results[i]?.status !== 'rejected') return false;
      if (!((results[i] as PromiseRejectedResult).reason instanceof OfflineError)) return false;
      useOffline.getState().enqueue({ kind: done ? 'habit.log' : 'habit.unlog', id: hid, date });
      return true;
    });
    const failed = targets.length - written.length - queued.length;

    if (failed > 0) {
      try {
        const fresh = await listHabits();
        set({ habits: fresh.habits.sort((a, b) => a.order - b.order), logs: fresh.logs });
      } catch {
        // The optimistic state stays; the next load() will settle it.
      }
    }
    return { written: [...written, ...queued], failed };
  },
  reorder: async (habitIds) => {
    const byId = new Map(get().habits.map((h) => [h.habitId, h]));
    const reordered = habitIds
      .map((id, i) => {
        const h = byId.get(id);
        return h ? { ...h, order: i } : null;
      })
      .filter((h): h is Habit => h !== null);
    set({ habits: reordered });
    const results = await Promise.allSettled(
      reordered.map((h) => updateHabit(h.habitId, { order: h.order }))
    );
    results.forEach((r, i) => {
      if (r.status !== 'rejected') return;
      if (!(r.reason instanceof OfflineError)) throw r.reason;
      const h = reordered[i]!;
      useOffline.getState().enqueue({ kind: 'habit.update', id: h.habitId, patch: { order: h.order } });
    });
  },
  isDone: (hid, date) =>
    get().logs.some((l) => l.habitId === hid && l.date === date),
}));
